import React, { useEffect, useState, useMemo } from 'react';
import { signUpWithEmail, signInWithEmail, sendPasswordResetEmail } from '../api/onboarding';
import { useOnboarding } from './OnboardingContext';
import { logOnboardingError } from '../api/errorLogger';

export function EmailSignupStep({ stepId }) {
  const {
    nextStep,
    markStepComplete,
    setAuthUser,
    setProfileDraft,
    profileDraft,
    setLoading,
    loading,
    prevStep,
  } =
    useOnboarding();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoginMode, setIsLoginMode] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [awaitingEmailConfirmation, setAwaitingEmailConfirmation] = useState(false);

  // Password validation state
  const [passwordFocused, setPasswordFocused] = useState(false);

  useEffect(() => {
    if (profileDraft.email) {
      setEmail(profileDraft.email);
    }
    if (profileDraft.full_name) {
      setUsername(profileDraft.full_name);
    }
  }, [profileDraft.email, profileDraft.full_name]);

  // Validate password requirements using useMemo to avoid unnecessary re-renders
  const passwordRequirements = useMemo(() => ({
    minLength: password.length >= 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  }), [password]);

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    try {
      const { error: resetError } = await sendPasswordResetEmail(email.trim());

      if (resetError) {
        setError(resetError.message);
      } else {
        setSuccess('Password reset email sent! Please check your inbox and follow the link to reset your password.');
      }
    } catch (err) {
      setError(err.message || 'Failed to send reset email. Please try again.');
      // Log unexpected errors
      await logOnboardingError({
        error: err,
        action: 'password_reset_unexpected',
        stepId,
        userEmail: email.trim(),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    setLoading(true);
    try {
      const trimmedEmail = email.trim();
      const trimmedPassword = password.trim();

      if (!trimmedEmail || !trimmedPassword) {
        setError('Please complete all fields.');
        return;
      }

      if (!isLoginMode) {
        if (!username || !username.trim()) {
          setError('Please enter your username.');
          return;
        }

        // Check all password requirements
        const allRequirementsMet = Object.values(passwordRequirements).every(req => req);
        if (!allRequirementsMet) {
          setError('Please meet all password requirements for better security.');
          return;
        }

        const { data, error: signUpError } = await signUpWithEmail({
          fullName: username.trim(),
          email: trimmedEmail,
          password: trimmedPassword,
        });

        if (signUpError) {
          setError(signUpError.message);
          return;
        }

        if (data?.user) {
          setAuthUser(data.user);
          setProfileDraft({
            full_name: username.trim(),
            email: trimmedEmail,
          });

          // If email confirmation required, show message and don't auto-proceed
          if (!data.session) {
            setAwaitingEmailConfirmation(true);
            markStepComplete(stepId);
            // Don't call nextStep() - let user manually continue after email confirmation
          } else {
            // Session exists, proceed immediately
            setSuccess('Account created successfully!');
            markStepComplete(stepId);
            nextStep();
          }
        } else {
          setError('Unexpected response from server. Please try again.');
        }
      } else {
        const { data, error: signInError } = await signInWithEmail({
          email: trimmedEmail,
          password: trimmedPassword,
        });

        if (signInError) {
          setError(signInError.message);
          return;
        }

        if (data?.session) {
          setSuccess('Welcome back! Redirecting to your dashboard…');
          markStepComplete(stepId);
          setAuthUser(data.session.user);
          nextStep();
        } else {
          setError('Unable to sign in. Please try again.');
        }
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      // Log unexpected errors (API errors are logged in onboarding.js)
      await logOnboardingError({
        error: err,
        action: isLoginMode ? 'signin_unexpected' : 'signup_unexpected',
        stepId,
        userEmail: email.trim(),
        metadata: { isLoginMode },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center px-4 py-8 sm:py-12">
      <div className="w-full max-w-3xl bg-slate-800/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-2xl border border-slate-700/50 overflow-hidden transition-transform">
        <div className="bg-gradient-to-r from-cyan-600 to-cyan-500 px-4 py-4 sm:px-6 sm:py-5 md:px-10 md:py-6 flex flex-col sm:flex-row justify-between sm:items-center gap-3 sm:gap-0 text-white">
          <div className="flex-1">
            <h2 className="text-xl sm:text-2xl font-semibold">
              {isForgotPassword ? 'Reset your password' : isLoginMode ? 'Welcome back' : 'Create your account'}
            </h2>
            <p className="text-xs sm:text-sm opacity-90 mt-1">
              {isForgotPassword
                ? 'Enter your email to receive a password reset link.'
                : isLoginMode
                ? 'Sign in to continue your personalized training journey.'
                : 'Secure your profile and unlock personalized training.'}
            </p>
          </div>
          <button
            type="button"
            onClick={prevStep}
            className="text-white text-sm font-medium underline underline-offset-4 hover:opacity-80 transition-opacity"
          >
            ← Back
          </button>
        </div>

        <div className="px-4 py-6 sm:px-6 sm:py-7 md:px-10 md:py-8">
          {!awaitingEmailConfirmation && error && (
            <div className="bg-red-900/50 border border-red-700/50 text-red-200 rounded-xl px-4 py-3 mb-4 text-sm">
              {error}
            </div>
          )}
          {!awaitingEmailConfirmation && success && (
            <div className="bg-green-900/50 border border-green-700/50 text-green-200 rounded-xl px-4 py-3 mb-4 text-sm">
              {success}
            </div>
          )}

          {awaitingEmailConfirmation ? (
            <div className="bg-green-900/50 border border-green-700/50 rounded-xl px-6 py-6">
              <p className="text-base text-green-100 mb-4 leading-relaxed">
                🎉 Account created successfully! Please check your email inbox for a confirmation link. Click the link in the email to verify your account, then return here to continue.
              </p>
              <p className="text-sm text-green-200 mb-4">
                Once you've confirmed your email, click the button below to continue setting up your profile.
              </p>
              <button
                type="button"
                onClick={nextStep}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-3 rounded-xl transition-colors"
              >
                Continue to Next Step
              </button>
            </div>
          ) : (
            <>
            <form onSubmit={isForgotPassword ? handleForgotPassword : handleSubmit} className="space-y-6">
            {!isLoginMode && !isForgotPassword && (
              <div className="grid gap-2">
                <label className="text-sm font-medium text-slate-200" htmlFor="username">
                  Username
                </label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600 text-white rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent placeholder-slate-400"
                  placeholder="felixcarter"
                  data-testid="onboarding-username"
                  autoComplete="off"
                />
              </div>
            )}

            <div className="grid gap-2">
              <label className="text-sm font-medium text-slate-200" htmlFor="signup-email">
                Email
              </label>
              <input
                id="signup-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600 text-white rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent placeholder-slate-400"
                placeholder="you@example.com"
                data-testid="onboarding-email"
                autoComplete="email"
                required
              />
            </div>

            {!isForgotPassword && (
              <div className="grid gap-2">
                <label className="text-sm font-medium text-slate-200" htmlFor="signup-password">
                  Password
                </label>
                <input
                  id="signup-password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                  className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600 text-white rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent placeholder-slate-400"
                  placeholder={isLoginMode ? 'Enter your password' : 'Create a strong password'}
                  data-testid="onboarding-password"
                  autoComplete={isLoginMode ? 'current-password' : 'new-password'}
                  required
                />
                {!isLoginMode && (passwordFocused || password.length > 0) && (
                  <div className="mt-2 p-3 bg-slate-700/50 rounded-lg border border-slate-600/50">
                    <p className="text-xs font-semibold text-slate-200 mb-2">Password must contain:</p>
                    <ul className="space-y-1">
                      <li className="flex items-center text-xs">
                        <span className={`mr-2 ${passwordRequirements.minLength ? 'text-green-400' : 'text-slate-500'}`}>
                          {passwordRequirements.minLength ? '✓' : '○'}
                        </span>
                        <span className={passwordRequirements.minLength ? 'text-green-300' : 'text-slate-400'}>
                          At least 8 characters
                        </span>
                      </li>
                      <li className="flex items-center text-xs">
                        <span className={`mr-2 ${passwordRequirements.hasUpperCase ? 'text-green-400' : 'text-slate-500'}`}>
                          {passwordRequirements.hasUpperCase ? '✓' : '○'}
                        </span>
                        <span className={passwordRequirements.hasUpperCase ? 'text-green-300' : 'text-slate-400'}>
                          One uppercase letter (A-Z)
                        </span>
                      </li>
                      <li className="flex items-center text-xs">
                        <span className={`mr-2 ${passwordRequirements.hasLowerCase ? 'text-green-400' : 'text-slate-500'}`}>
                          {passwordRequirements.hasLowerCase ? '✓' : '○'}
                        </span>
                        <span className={passwordRequirements.hasLowerCase ? 'text-green-300' : 'text-slate-400'}>
                          One lowercase letter (a-z)
                        </span>
                      </li>
                      <li className="flex items-center text-xs">
                        <span className={`mr-2 ${passwordRequirements.hasNumber ? 'text-green-400' : 'text-slate-500'}`}>
                          {passwordRequirements.hasNumber ? '✓' : '○'}
                        </span>
                        <span className={passwordRequirements.hasNumber ? 'text-green-300' : 'text-slate-400'}>
                          One number (0-9)
                        </span>
                      </li>
                      <li className="flex items-center text-xs">
                        <span className={`mr-2 ${passwordRequirements.hasSpecialChar ? 'text-green-400' : 'text-slate-500'}`}>
                          {passwordRequirements.hasSpecialChar ? '✓' : '○'}
                        </span>
                        <span className={passwordRequirements.hasSpecialChar ? 'text-green-300' : 'text-slate-400'}>
                          One special character (!@#$%^&*)
                        </span>
                      </li>
                    </ul>
                  </div>
                )}
                {isLoginMode && !isForgotPassword && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsForgotPassword(true);
                      setIsLoginMode(false);
                      setError('');
                      setSuccess('');
                    }}
                    className="text-sm text-cyan-400 hover:text-cyan-300 font-medium text-left"
                  >
                    Forgot your password?
                  </button>
                )}
              </div>
            )}

            <button
              type="submit"
              data-testid="onboarding-submit"
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-semibold px-4 py-3 rounded-xl transition-colors shadow-lg shadow-cyan-500/20"
              disabled={loading}
            >
              {loading
                ? isForgotPassword
                  ? 'Sending reset email…'
                  : isLoginMode
                  ? 'Logging in…'
                  : 'Creating account…'
                : isForgotPassword
                ? 'Send Reset Email'
                : isLoginMode
                ? 'Log In'
                : 'Create Account'}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-400">
            {isForgotPassword ? (
              <button
                type="button"
                className="text-cyan-400 hover:text-cyan-300 font-medium underline underline-offset-2"
                onClick={() => {
                  setIsForgotPassword(false);
                  setIsLoginMode(true);
                  setError('');
                  setSuccess('');
                }}
              >
                ← Back to login
              </button>
            ) : (
              <>
                {isLoginMode ? 'Need an account?' : 'Already have an account?'}{' '}
                <button
                  type="button"
                  className="text-cyan-400 hover:text-cyan-300 font-medium underline underline-offset-2"
                  onClick={() => {
                    setError('');
                    setSuccess('');
                    setIsLoginMode((prev) => !prev);
                  }}
                >
                  {isLoginMode ? 'Sign up' : 'Log in'}
                </button>
              </>
            )}
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
